import { ReactElement, Key, useEffect, useRef, useState } from 'react';
import { classNames } from '@/shared/utils/componentHelpers';
import {
	TableContainer as MuiTableContainer,
	Table as MuiTable,
	TableBody as MuiTableBody,
	TableHead as MuiTableHead,
	TableRow as MuiTableRow,
	TableCell as MuiTableCell,
	Stack,
} from '@mui/material';
import TableActionsCell from './components/TableActionsCell';
import useCellRender from './useCellRender';
import { TableProps } from './types';
import './table.scss';

const Table = <DataModel extends object>({
	ariaLabel,
	data,
	columns,
	actionsColumn,
	empty,
	rowAction,
}: TableProps<DataModel>): ReactElement => {
	const tableRef = useRef<HTMLDivElement | null>(null);
	const { renderTableCell, renderActionsCell } = useCellRender<DataModel>();
	const [hasHorizontalScroll, setHasHorizontalScroll] = useState<boolean>(false);

	const renderTableRow = (item: DataModel, key: Key) => (
		<MuiTableRow
			key={key}
			component="tr"
			onClick={() => rowAction?.(item)}
			className={rowAction ? 'interactive-table-row' : ''}
		>
			{columns.map((column) => (
				<MuiTableCell key={column.name} align={column.align}>
					{renderTableCell(column, item)}
				</MuiTableCell>
			))}
			{actionsColumn && (
				<TableActionsCell align="right" key="actions-cell" className="table-actions-cell">
					<Stack spacing={1} direction="row" justifyContent="flex-end">
						{renderActionsCell(actionsColumn, item)}
					</Stack>
				</TableActionsCell>
			)}
		</MuiTableRow>
	);

	const updateScrollState = () => {
		const table = tableRef.current;
		if (table) {
			const hasHorizontalScroll = table.scrollWidth > table.clientWidth;
			setHasHorizontalScroll(hasHorizontalScroll);
		}
	};

	useEffect(() => {
		updateScrollState();

		window.addEventListener('resize', updateScrollState);
		return () => window.removeEventListener('resize', updateScrollState);
	}, []);

	return (
		<MuiTableContainer
			ref={tableRef}
			className={classNames(hasHorizontalScroll && 'table-with-horizontal-scroll')}
		>
			<MuiTable aria-label={ariaLabel}>
				<MuiTableHead>
					<MuiTableRow>
						{columns.map((column) => (
							<MuiTableCell
								key={column.name}
								align={column.align}
								style={{ minWidth: column.minWidth ? `${column.minWidth}px` : undefined }}
							>
								{column.name}
							</MuiTableCell>
						))}
						{actionsColumn && (
							<MuiTableCell align="right" className="table-actions-cell">
								{actionsColumn.name}
							</MuiTableCell>
						)}
					</MuiTableRow>
				</MuiTableHead>
				<MuiTableBody>
					{!data || data.length === 0 ? (
						<MuiTableRow>
							<MuiTableCell colSpan={50}>{empty}</MuiTableCell>
						</MuiTableRow>
					) : (
						data.map((item, key) => renderTableRow(item, key))
					)}
				</MuiTableBody>
			</MuiTable>
		</MuiTableContainer>
	);
};

export default Table;
